import { useMutation, useQuery } from '@apollo/client'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material'
import { useState } from 'react'

import {
  CREATE_DAI_GROUP,
  CreateDaiGroupMutation,
  CreateDaiGroupMutationVariables,
  GET_PERMISSIONS,
  GET_TENANT_GROUPS,
  GetPermissionsQuery,
  GetPermissionsQueryVariables,
  GetTenantGroupsQuery,
  GetTenantGroupsQueryVariables,
} from '@/graphql'
import useT from '@/i18n/useT'

import { DAISelect } from '../components/DAISelect'
import { PermissionsSelectionList } from './PermissionsSelectionList'

type Props = {
  open: boolean
  onClose: () => void
}

export const CreateRoleDialog: React.FC<Props> = props => {
  const { open, onClose } = props
  const { t } = useT()

  const [name, setName] = useState('')
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>(
    [],
  )
  const [supervisingRoleId, setSupervisingRoleId] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)

  const permissionsData = useQuery<
    GetPermissionsQuery,
    GetPermissionsQueryVariables
  >(GET_PERMISSIONS)

  const groupsData = useQuery<
    GetTenantGroupsQuery,
    GetTenantGroupsQueryVariables
  >(GET_TENANT_GROUPS)

  const [createDaiGroup, { loading: creatingGroup }] = useMutation<
    CreateDaiGroupMutation,
    CreateDaiGroupMutationVariables
  >(CREATE_DAI_GROUP)

  const togglePermission = (permissionId: string) => {
    setSelectedPermissionIds(
      selectedPermissionIds.includes(permissionId)
        ? selectedPermissionIds.filter(id => id !== permissionId)
        : [...selectedPermissionIds, permissionId],
    )
  }

  const handleCreateGroup = async () => {
    setCreateError(null)
    try {
      await createDaiGroup({
        variables: {
          data: {
            name: name.trim(),
            permissionIds: selectedPermissionIds,
            supervisingRoleId: supervisingRoleId || null,
          },
        },
        refetchQueries: [{ query: GET_TENANT_GROUPS }],
        awaitRefetchQueries: true,
      })
      handleClose()
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : String(error))
    }
  }

  const handleClose = () => {
    setName('')
    setSelectedPermissionIds([])
    setSupervisingRoleId('')
    setCreateError(null)
    onClose()
  }

  const permissions = permissionsData.data?.permissions || []
  const groups = groupsData.data?.tenantGroups.results || []

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle>
        {t('manageRoles.createDialog.title', 'Create Role')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} paddingTop={2}>
          <TextField
            label={t('manageRoles.createDialog.name', 'Role Name')}
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            autoFocus
          />
          <DAISelect
            title={t(
              'manageRoles.createDialog.supervisingRole',
              'Supervising Role',
            )}
            value={supervisingRoleId}
            handleChange={setSupervisingRoleId}
            options={[
              { value: '', label: '-' },
              ...groups.map(group => ({
                value: group.id,
                label: group.name,
              })),
            ]}
          />
          {permissionsData.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : permissionsData.error ? (
            <Alert severity="error">{permissionsData.error.message}</Alert>
          ) : (
            <PermissionsSelectionList
              permissions={permissions}
              selectedPermissionIds={selectedPermissionIds}
              onTogglePermission={togglePermission}
            />
          )}
          {createError && <Alert severity="error">{createError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          loading={creatingGroup}
          loadingPosition="end"
          disabled={!name.trim()}
          onClick={handleCreateGroup}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
